import { Property } from '@codefluegel/wowis-rest-typescript-client';
import {
  Badge,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useMessagesApi, useTopApi, useWelcomeApi } from '../../ApiClient';
import { useStore } from '../../store/context';

const RealEstatesMenu: FC = () => {
  const { palette } = useTheme();

  const { welcomeStore, messageStore } = useStore();
  const welcomeApi = useWelcomeApi();
  const topApi = useTopApi();
  const messageApi = useMessagesApi();

  const { currentEstate } = welcomeStore;

  const estateObjects = welcomeStore.getWelcomeData()?.objects || [];
  const [objectTypes, setObjectTypes] = useState<{ [key: number]: string }>({});

  async function fetchObjectType(topId: number) {
    try {
      const { data } = await topApi.getTopArt(topId);
      setObjectTypes(prevData => ({
        ...prevData,
        [topId]: data.kurzbezeichnung ?? '',
      }));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (Object.keys(objectTypes).length === 0) {
      estateObjects.forEach(element => {
        fetchObjectType(element.topId);
      });
    }
  }, [fetchObjectType, estateObjects, objectTypes]);

  async function fetchMessages() {
    if (welcomeStore.currentEstate) {
      try {
        const { data } = await messageApi.getAllCustomerMessages(
          welcomeStore.currentEstate.customerId,
        );
        messageStore.setMessages(data);
      } catch (error) {
        console.log(error);
      }
    }
  }

  async function fetchWelcomeData() {
    try {
      const { data } = await welcomeApi.getWelcomeData(
        welcomeStore.currentEstateId,
      );
      welcomeStore.setWelcomeData(data);
      data.objects?.forEach(element => {
        fetchObjectType(element.objId);
      });
      fetchMessages();
    } catch (error) {
      // TODO: add error handling
      console.log(error);
    }
  }

  const handleChange = (event: SelectChangeEvent) => {
    welcomeStore.setWelcomeData(undefined);
    welcomeStore.currentEstateId = Number(event.target.value);
    fetchWelcomeData();
  };

  const selectFieldStyles = {
    paddingLeft: 1,
    '.MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput': {
      color: palette.primary.main,
    },
    '.MuiSvgIcon-root ': {
      fill: palette.primary.main,
    },
    // highlight selected item on hover with color
    '& .MuiMenuItem-root.Mui-selected': {
      backgroundColor: palette.primary.main,
      color: palette.primary.contrastText,
    },
  };

  if (!estateObjects.length) return null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Typography
        variant="body1"
        component="p"
        textAlign="center"
        color="secondary"
        sx={{
          margin: -1,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          letterSpacing: 1,
          mt: 3,
        }}
      >
        Immobilie:
      </Typography>
      <Select
        sx={selectFieldStyles}
        value={currentEstate?.customerId.toString() || ''}
        label="Immobilie"
        color="secondary"
        onChange={handleChange}
        IconComponent={KeyboardArrowDownIcon}
      >
        {estateObjects
          .sort((a, b) => (a.city ?? '').localeCompare(b.city ?? ''))
          .map((realEstate: Property) => (
            <MenuItem
              key={realEstate.customerId}
              value={realEstate.customerId}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Badge badgeContent={0} color="primary">
                <Box display="flex" flexDirection="column">
                  <Typography
                    component="span"
                    variant="body2"
                    color="secondary"
                    sx={{
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      letterSpacing: 1,
                    }}
                  >
                    {`Top ${realEstate.topNr}${
                      realEstate.tuer ? `, Tür ${realEstate.tuer}` : ''
                    } ${objectTypes[realEstate.topId] ?? ''}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    color="secondary"
                    sx={{
                      textTransform: 'uppercase',
                      textAlign: 'center',
                    }}
                  >{`${realEstate.street ?? ''}`}</Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="secondary"
                    sx={{
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      letterSpacing: 1,
                    }}
                  >
                    {`${realEstate.zip} ${realEstate.city}`}
                  </Typography>
                </Box>
              </Badge>
            </MenuItem>
          ))}
      </Select>
    </Box>
  );
};

RealEstatesMenu.defaultProps = {
  unreadDocuments: 0,
};

export default RealEstatesMenu;
